import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { SearchResponse, TypeSearch, walletRepository } from '../../../../repositories/WalletRepository';
import { useAuth } from '../../../../hooks/useAuth';
import { OPAAvatar } from '../../../../components/OPAAvatar';
import { LatestRecipients } from './LatestRecipients';
import { Pleb } from '../../../../models/Pleb';
import { SendTransactionStateRecipient } from '../../../../store/slices/transactionSlice';
import { BulkPayment, BulkTypes, getDisplayNameBulkPayment } from '../../../../models/BulkPayment';
import { formatAmount, getFlagEmoji } from '../../../../utils/Utils';

interface InputSendWalletProps {
    onChangeLnurl: (lnurl: string) => void;
    onSelectLnurl: (lnurl: string) => void;
    onChangePleb: (pleb: Pleb) => void;
    onSelectPleb: (pleb: Pleb) => void;
    onSelectBulkPayment: (bulkPayment: BulkPayment) => void;
    defaultRecipient?: SendTransactionStateRecipient | null;
    timestamp?: number;
    showRecentRecipients?: boolean;
    placeholder?: string;
    membersOnly?: boolean;
}

export const getNameToShow = (recipient?: SendTransactionStateRecipient | null): string => {
    if(recipient?.pleb) {
        return recipient.pleb.getLabelToShowSendToWallet();
    }
    else if(recipient?.lnurl) {
        return recipient.lnurl;
    }
    else if(recipient?.invoice) {
        return recipient.invoice;
    }
    return '';
}

// Same delay as the mobile app, so we search once the user stops typing
const SEARCH_DEBOUNCE_MS = 500;

// Same as the mobile app: the count is shown in grey after the name
const RecipientName: React.FC<{ name?: string; recipientsCount?: number }> = ({ name, recipientsCount }) => (
    <span>
        {name}
        {recipientsCount !== undefined && recipientsCount > 0 && (
            <span className="opacity-50"> ({formatAmount(recipientsCount)})</span>
        )}
    </span>
);

export const InputSendWallet: React.FC<InputSendWalletProps> = ({ onChangeLnurl, onSelectLnurl, onChangePleb, onSelectPleb, onSelectBulkPayment, defaultRecipient, timestamp, showRecentRecipients, placeholder, membersOnly }) => {
    const [value, setValue] = useState(getNameToShow(defaultRecipient));
    const [isFocused, setIsFocused] = useState(false);
    const [showListbox, setShowListbox] = useState(false);
    const {pleb} = useAuth();
    const [searchResponses, setSearchResponses] = useState<SearchResponse[]>([]);
    const [plebSelected, setPlebSelected] = useState<Pleb | null>(defaultRecipient?.pleb ?? null);
    // Set when picking a result fills the input, so that name isn't searched again
    const skipNextSearch = useRef(false);

    useEffect(() => {
        if(timestamp) {
            setValue('');
            setPlebSelected(null);
        }
    }, [timestamp]);
    
    useEffect(() => {
        const query = value.trim();

        if (skipNextSearch.current || query.length === 0) {
            skipNextSearch.current = false;
            setSearchResponses([]);
            return;
        }

        // Create new abort controller for each effect
        const abortController = new AbortController();

        const searchWallet = async () => {
            try {
                const searchResponses = await walletRepository.searchWallet(
                    pleb?.id ?? '',
                    query,
                    membersOnly,
                    abortController.signal
                );
                setSearchResponses(searchResponses);
            } catch (error) {
                // Ignore aborted requests
                if (axios.isCancel(error)) return;
                console.error('Search error:', error);
            }
        };

        const timeout = setTimeout(searchWallet, SEARCH_DEBOUNCE_MS);

        // Cleanup: cancel the pending search when value changes or component unmounts
        return () => {
            clearTimeout(timeout);
            abortController.abort();
        };
    }, [value, pleb?.id, membersOnly]);

    useEffect(() => {
        if(plebSelected) {
            onChangePleb(plebSelected);
        } else {
            onChangeLnurl(value);
        }
    }, [plebSelected, value, onChangeLnurl, onChangePleb]);

    const onClear = () => {
        skipNextSearch.current = false;
        setValue('');
        setPlebSelected(null);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        setShowListbox(true);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        // Delay hiding listbox to allow click events
        setTimeout(() => setShowListbox(false), 200);
    };

    const handleSelectSearchResponse = (searchResponse: SearchResponse) => {
        switch(searchResponse.type) {
            case TypeSearch.user:
                handleSelectPleb(searchResponse.user);
                break;
            case TypeSearch.bulk_special:
                handleSelectBulkPayment(searchResponse.bulkSpecial);
                break;
            case TypeSearch.city:
                handleSelectBulkPayment({
                    id: searchResponse.city?.id ?? '',
                    type: BulkTypes.city,
                    name: searchResponse.city?.name ?? '',
                    recipientsCount: 0,
                    filters: searchResponse.filters ?? undefined
                });
                break;
            case TypeSearch.distanceFromMyself:
                handleSelectBulkPayment({
                    id: searchResponse.distanceFromMyself?.id ?? '',
                    type: BulkTypes.distanceFromMyself,
                    name: searchResponse.distanceFromMyself?.name ?? '',
                    recipientsCount: 0,
                });
                break;
            case TypeSearch.userAttribute:
                handleSelectBulkPayment({
                    id: searchResponse.bulkSpecial?.id ?? '',
                    type: BulkTypes.userAttribute,
                    name: searchResponse.bulkSpecial?.name ?? '',
                    recipientsCount: searchResponse.bulkSpecial?.recipientsCount ?? 0,
                });
                break;
        }
    }

    const handleSelectPleb = (element?: Pleb) => {
        if(element) {
            skipNextSearch.current = true;
            setValue(element.getLabelToShowSendToWallet());
            setPlebSelected(element);
            setShowListbox(false);
            onSelectPleb(element);
        }
    };

    const handleSelectBulkPayment = (element?: BulkPayment) => {
        if(element) {
            skipNextSearch.current = true;
            setValue(getDisplayNameBulkPayment(element));
            setPlebSelected(null);
            setShowListbox(false);
            onSelectBulkPayment(element);
        }
    };

    const handleSelectLnurl = (lnurl: string) => {
        skipNextSearch.current = true;
        setValue(lnurl);
        setPlebSelected(null);
        setShowListbox(false);
        onSelectLnurl(lnurl);
    };

    const handlePaste = async () => {
        try {
          const clipboardText = await navigator.clipboard.readText();
          if (clipboardText) {
            skipNextSearch.current = false;
            setPlebSelected(null);
            setValue(clipboardText);
          }
        } catch (err) {
          console.error('Clipboard access failed:', err);
        }
    };

    return (
        <div className="relative">
            <div className="relative">
                <input
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className={`
                        w-full
                        px-4
                        py-3
                        pe-9
                        bg-brand-bg-3
                        rounded-xl
                        placeholder:text-brand-color-2
                        focus:outline-none
                        ${isFocused ? '' : ''}
                    `}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => {
                        skipNextSearch.current = false;
                        setPlebSelected(null);
                        setValue(e.target.value);
                    }}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    inputMode="text"
                    name="search-wallet"
                />
                {value !== '' && (
                    <button 
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-transparent hover:opacity-70 focus:outline-none flex flex-col text-[20px] p-2"
                        onClick={() => onClear()}
                        type="button"
                    >
                        <i className="material-icons">cancel</i>
                    </button>
                )}
                {value === '' && (
                    <button 
                        className="text-brand-color-2 absolute right-0 top-1/2 transform -translate-y-1/2 bg-transparent hover:opacity-70 focus:outline-none flex flex-col text-[20px] p-2"
                        onClick={handlePaste}
                        type="button"
                    >
                        <i className="material-icons">content_paste</i>
                    </button>
                )}
            </div>

            {/* Listbox dropdown */}
            {showListbox && searchResponses.length > 0 && (
                <div className="w-full mt-1 rounded-lg z-50 max-h-60 overflow-y-auto">
                {searchResponses.map((searchResponse, index) => {
                    switch(searchResponse.type) {
                        case TypeSearch.user:
                            return (
                                <div
                                    key={index}
                                    onClick={() => handleSelectSearchResponse(searchResponse)}
                                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    <OPAAvatar
                                        imageUrl={searchResponse.user?.thumbnail}
                                        size={40}
                                        borderWidth={0}
                                        borderColor="var(--brand-bg-2)"
                                    />
                                    {searchResponse.user?.name}
                                </div>
                            );
                        case TypeSearch.bulk_special:
                            return (
                                <div 
                                    key={index} 
                                    onClick={() => handleSelectSearchResponse(searchResponse)}
                                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    {
                                        searchResponse?.imgUrl ? (
                                            <OPAAvatar
                                                imageUrl={searchResponse?.imgUrl}
                                                size={40}
                                                borderWidth={0}
                                                borderColor="var(--brand-bg-2)"
                                            />
                                        ) : (
                                            null
                                        )
                                    }
                                    <RecipientName name={searchResponse.bulkSpecial?.name} recipientsCount={searchResponse.bulkSpecial?.recipientsCount} />
                                </div>
                            );
                        case TypeSearch.city:
                            return (
                                <div 
                                    key={index} 
                                    onClick={() => handleSelectSearchResponse(searchResponse)}
                                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center overflow-hidden">
                                        {
                                            searchResponse.city?.geoZap?.addressType === 'country' ? (
                                                searchResponse.city.geoZap.countryImgUrl ? (
                                                    <img src={searchResponse.city.geoZap.countryImgUrl} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-2xl">{getFlagEmoji(searchResponse.city?.geoZap?.countryCode ?? '')}</span>
                                                )
                                            ) : (
                                                <span className="material-symbols-outlined">location_city</span>
                                            )
                                        }
                                    </div>
                                    <RecipientName name={searchResponse.city?.name} recipientsCount={searchResponse.city?.recipientsCount} />
                                </div>
                            );
                            case TypeSearch.distanceFromMyself:
                                return (
                                    <div 
                                        key={index} 
                                        onClick={() => handleSelectSearchResponse(searchResponse)}
                                        className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                    >
                                        <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center">
                                            <span className="material-symbols-outlined">location_on</span>
                                        </div>
                                        {searchResponse.distanceFromMyself?.name}
                                    </div>
                                );
                            case TypeSearch.userAttribute:
                                return (
                                    <div 
                                        key={index} 
                                        onClick={() => handleSelectSearchResponse(searchResponse)}
                                        className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                    >
                                        <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center">
                                            <span className="material-symbols-outlined">label</span>
                                        </div>
                                        <RecipientName name={searchResponse.bulkSpecial?.name} recipientsCount={searchResponse.bulkSpecial?.recipientsCount} />
                                    </div>
                                );
                    }
                })}
                </div>
            )}

            {showRecentRecipients && (
                <LatestRecipients 
                    onSelectLnurl={(lnurl: string) => handleSelectLnurl(lnurl)}
                    onSelectBulkPayment={(bulkPayment: BulkPayment) => handleSelectBulkPayment(bulkPayment)}
                    className={`${value !== "" || (showListbox && searchResponses.length > 0) ? 'hidden' : ''}`}
                />
            )}
        </div>
    );
};